import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import {
  RktTableSearchComponent,
  RktTableComponent,
  FetchData,
  DatatableSorting,
  DatatableColumn,
} from '@rocketfinancialcorp/rocket-ui/table';
import { FilterMenuComponent } from '@shared/components';
import { FocusRemoveDirective } from '@shared/directives';
import { FilterField, FilterValues, RequestPageParams, TransactionListItem } from '@shared/models';
import { TransactionService } from '@shared/services';
import { identity, isUndefined, pickBy } from 'lodash-es';
import { transactionFeature, TransactionsActions } from 'src/app/+state/transaction-feature';

@Component({
  selector: 'app-transactions-list',
  standalone: true,
  imports: [CommonModule, RktIconComponent, FilterMenuComponent, RktTableSearchComponent, RktTableComponent, FocusRemoveDirective],
  templateUrl: './transactions-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionListComponent {
  @ViewChild('filterMenu') filterMenu!: FilterMenuComponent;

  store = inject(Store);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  transactionService = inject(TransactionService);

  searchString?: string;

  filters: FilterField[] = [
    {
      name: 'date',
      displayName: 'Date',
      type: 'DATE',
      options: [
        { label: 'Default: 30 days', value: 'DEFAULT' },
        { label: 'Today', value: 'TODAY' },
        { label: 'Yesterday', value: 'YESTERDAY' },
        { label: 'This week', value: 'THIS_WEEK' },
        { label: 'This month', value: 'THIS_MONTH' },
        { label: 'Custom range', value: 'CUSTOM' },
      ],
      defaultValue: 'DEFAULT',
      props: {
        maxMonths: '15',
      },
    },
    {
      name: 'businessAccountId',
      displayName: 'BA ID',
      type: 'INPUT',
    },
    {
      name: 'transactionId',
      displayName: 'Txn ID',
      type: 'INPUT',
    },
    {
      name: 'debitFinancialAccountId',
      displayName: 'Debit Account ID',
      type: 'INPUT',
    },
    {
      name: 'creditFinancialAccountId',
      displayName: 'Credit Account ID',
      type: 'INPUT',
    },
    {
      name: 'maskedDebitAccountNumber',
      displayName: 'Debit Account No.',
      type: 'INPUT',
      props: {
        maxLength: '20',
        validationPattern: '[0-9*]*',
        validationPatternMessage: 'Account Number only allow numbers (digits) and asterisk',
      },
    },
    {
      name: 'maskedCreditAccountNumber',
      displayName: 'Credit Account No.',
      type: 'INPUT',
      props: {
        maxLength: '20',
        validationPattern: '[0-9*]*',
        validationPatternMessage: 'Account Number only allow numbers (digits) and asterisk',
      },
    },
    {
      name: 'amount',
      displayName: 'Amount',
      type: 'AMOUNT',
    },
    {
      name: 'currency',
      displayName: 'Currency',
      type: 'INPUT',
    },
    {
      name: 'latestStatus',
      displayName: 'Status',
      type: 'RADIO',
      options: [
        { label: 'All', value: undefined },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Cleared', value: 'CLEARED' },
        { label: 'Declined', value: 'DECLINED' },
        { label: 'Error', value: 'ERROR' },
        { label: 'New', value: 'NEW' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Processing', value: 'PROCESSING' },
        { label: 'Settled', value: 'SETTLED' },
        { label: 'Reversed', value: 'REVERSED' },
      ],
    },
    {
      name: 'origin',
      displayName: 'Transaction origin',
      type: 'RADIO',
      options: [
        { label: 'All', value: undefined },
        { label: 'Single', value: 'SINGLE' },
        { label: 'Batch', value: 'BATCH' },
      ],
    },
    {
      name: 'solution',
      displayName: 'Transaction Solution',
      type: 'RADIO',
      options: [
        { label: 'All', value: undefined },
        { label: 'ACH', value: 'ach' },
        { label: 'Push To Card', value: 'push-to-card' },
        { label: 'Transfer', value: 'transfer' },
        { label: 'Wire', value: 'wire' },
        { label: 'Millikart Transaction', value: 'millikart-transaction' },
        { label: 'I2C Transaction', value: 'i2c-transaction' },
        { label: 'Cards Transaction', value: 'cards-transaction' },
        { label: 'Financial Account Aggregation', value: 'financial-account-aggregation' },
      ],
    },
    {
      name: 'transactionTypes',
      displayName: 'Transaction Types',
      type: 'RADIO',
      options: [
        { label: 'All', value: undefined },
        { label: 'Send', value: 'SEND' },
        { label: 'Request', value: 'REQUEST' },
      ],
    },
  ];

  columns: DatatableColumn[] = [
    { name: 'Created At', prop: 'createdAt', fixedSize: 150, isSortable: true, colType: 'date-time' },
    { name: 'Transaction ID', prop: 'id', colType: 'short-id', flexSize: 160 },
    { name: 'Move From', prop: 'moveFrom', colType: 'two-line-col', flexSize: 315 },
    { name: 'Move To', prop: 'moveTo', colType: 'two-line-col', flexSize: 315 },
    { name: 'Solution', prop: 'solutionName', flexSize: 160 },
    { name: 'Type', prop: 'transactionType', flexSize: 120 },
    { name: 'Amount', prop: 'amount', flexSize: 151, isSortable: true, colType: 'amount', colAlign: 'right' },
    {
      name: 'Status',
      prop: 'status',
      sortProp: 'latestStatus',
      fixedSize: 140,
      isSortable: true,
      colType: 'status-tag',
      colAlign: 'right',
    },
  ];

  sortParams: DatatableSorting = { key: 'createdAt', sortProp: 'createdAt', sortDir: 'desc' };

  emptyListMessage = 'No matching Transactions found.';

  get activePage(): number {
    const routePage = this.activatedRoute.snapshot.queryParamMap.get('page');
    return routePage ? parseInt(routePage, 10) : 0;
  }

  transactions$ = this.store.select(transactionFeature.selectTransactions);

  totalElements$ = this.store.select(transactionFeature.selectTransactionsTotalElements);

  isLoading$ = this.store.select(transactionFeature.selectIsLoading);

  activeFilters$ = this.store.select(transactionFeature.selectTransactionsActiveFilters);

  activeFiltersCount$ = this.store.select(transactionFeature.selectTransactionsActiveFiltersCount);

  onSearch(event?: string): void {
    if (!event) {
      this.onSearchReset();
      return;
    }
    this.searchString = event;
    this.getTransactions({ page: 0 });
  }

  onSearchReset(): void {
    if (!this.searchString) {
      return;
    }
    this.searchString = undefined;
    this.getTransactions({ page: this.activePage });
  }

  updateQueryParams(page?: RequestPageParams['page']): void {
    if (!isUndefined(page) && this.activePage !== page) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { page: page > 0 ? page : undefined },
        queryParamsHandling: 'merge',
      });
    }
  }

  getTransactions({ page, size, sortParams }: FetchData): void {
    if (sortParams) {
      this.sortParams = sortParams;
    }
    this.updateQueryParams(page);

    this.store.dispatch(
      TransactionsActions.loadTransactions({
        params: {
          page: page || 0,
          size,
          searchString: this.searchString,
          sortParams: sortParams ?? this.sortParams,
        },
      }),
    );
  }

  onRowClick({ id }: TransactionListItem) {
    this.router.navigateByUrl(`/app/transactions/transactions/${id}`);
  }

  applyFilters(filterValues: FilterValues): void {
    this.store.dispatch(TransactionsActions.setTransactionsActiveFilters({ filters: { ...pickBy(filterValues, identity) } }));
    this.getTransactions({ page: 0 });
  }

  closeMenu(): void {
    if (this.filterMenu?.isVisible) {
      this.filterMenu.toggle({} as MouseEvent);
    }
  }
}
